import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'
import Alert from '~/Common-Objects/UI/Alert'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import CheckboxTile from '~/Common-Objects/UI/Forms/CheckboxTile'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import axios from '~/data/http/axios'
import StatusGlossary from '~/pages/cp/includes/glossary/status'
import { alterFullpage } from '~/data/actions/siteActions'
import PostHogFunctions from '~/Classes/posthog'
import queryString from 'query-string'
import helpers from '~/Classes/helpers'

export default function CapsuleSetupContacts({ groupId, totalContacts, updateAppSettings = () => {} }) {
    const [selected, setSelected] = useState('')
    const [filteredContacts, setFilteredContacts] = useState(undefined)
    const [tags, setTags] = useState(null)
    const [selectedTags, setSelectedTags] = useState([])
    const [processing, setProcessing] = useState(false)
    const history = useHistory()
    const dispatch = useDispatch()

    const selectTag = (value) => {
        const arr = [...selectedTags]
        const index = arr.indexOf(value)
        if (index === -1) {
            arr.push(value)
        } else {
            arr.splice(index, 1)
        }
        setSelectedTags(arr)
    }

    const next = async () => {
        PostHogFunctions.capture('onboarding_contacts_selected', {
            selected: selected,
            tags: selected === 'tags' ? selectedTags.length : undefined,
            filteredContacts: selected === 'tags' ? filteredContacts : undefined,
            totalContacts: totalContacts
        })
        if (selected === 'tags' && selectedTags?.length > 0) {
            setProcessing(true)
            // create a segment with this group and not these tags
            const queryObj = {
                and: [
                    {
                        customFieldId: 'GroupId',
                        operator: '=',
                        value: groupId
                    }
                ],
                or: []
            }
            queryObj.and.push({
                customFieldId: 'subscriberTags',
                operator: '!=',
                value: selectedTags
            })
            const segmentId = (
                await axios.post('/segment', {
                    name: 'Capsule Onboarding - Inactive Contacts',
                    temporary: true,
                    queryObj,
                    ignoreOrg: true
                })
            ).data.Segment.segmentId

            await updateAppSettings({
                onboardingSelectedTags: selectedTags,
                onboardingSelectedContacts: filteredContacts
            })

            // then POST to /subscriber/bulk/changestatus with the segment id and status in-active
            await axios.put('/subscriber/bulk/changestatus', {
                segmentId: segmentId,
                status: 'In-Active',
                GroupId: groupId,
                forceSql: true
            })
            setProcessing(false)
        }
        const query = queryString.parse(location.search)
        if (query.redirect) {
            helpers.safeRedirect(query.redirect, 'self', history)
        } else {
            history.push('/cp')
        }
    }

    const showStatusGlossary = () => {
        const modal = <StatusGlossary close={() => dispatch(alterFullpage(null))} />
        dispatch(alterFullpage(modal))
    }

    useEffect(() => {
        PostHogFunctions.capture('onboarding_sync_select_marketing_contacts')
        axios.get('/tags?totals=true&GroupId=' + groupId).then((res) => {
            setTags(res.data)
        })
    }, [])

    let total
    if (selected === 'everyone') {
        total = totalContacts
    }
    if (selected === 'tags') {
        if (selectedTags.length === 0) {
            total = 0
        } else {
            total = filteredContacts
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            <div className='row'>
                <div className='col-xs-6'>
                    <CheckboxTile
                        name='everyone'
                        value='everyone'
                        checked={selected === 'everyone'}
                        onChange={() => setSelected('everyone')}
                        outerStyle={{ height: '100%' }}
                        data-ph={'capsule_onboarding_everyone'}
                        header={'Everyone'}
                        subheader={'Start with all Capsule contacts as active marketing contacts.'}
                        icon='people'
                    />
                </div>
                <div className='col-xs-6'>
                    <CheckboxTile
                        name='tags'
                        value='tags'
                        checked={selected === 'tags'}
                        onChange={() => setSelected('tags')}
                        outerStyle={{ height: '100%' }}
                        data-ph={'capsule_onboarding_filtered'}
                        header={'Select using Tags'}
                        subheader={'Start with contacts that have specific tags to set as active marketing contacts.'}
                        icon='tagOutline'
                    />
                </div>
            </div>
            {selected === 'tags' && (
                <Tags
                    groupId={groupId}
                    selected={selectedTags}
                    tags={tags}
                    onSetTotal={setFilteredContacts}
                    onChange={selectTag}
                />
            )}
            {selected !== '' && (
                <>
                    <div
                        style={{
                            borderRadius: 8,
                            backgroundColor: 'var(--neutral-100)',
                            padding: '12px 16px 12px 16px',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 8,
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                        }}
                    >
                        {total === undefined && <LoadingIcons className='sm' />}
                        {total !== undefined && (
                            <>
                                <div
                                    style={{
                                        fontWeight: 500,
                                        fontSize: 14,
                                        lineHeight: '18px'
                                    }}
                                >
                                    {FormatNumberFuncs.formatNumber(total)} contacts selected
                                </div>
                                <Icons
                                    icon='questionCircleOutline'
                                    style={{ height: 16, width: 16 }}
                                    color='var(--neutral-800)'
                                    data-tooltip-html={`<div>
                                            <div style="text-align:center;">Contacts are counted once, even if they match multiple tags.</div>
                                            <div style="text-align:center;">Only contacts with unique email addresses are included.</div>
                                        </div>`}
                                    data-tooltip-id='contact_count'
                                    data-tooltip-style={{ zIndex: 99999 }}
                                />
                            </>
                        )}
                    </div>
                    <Button
                        className='button-primary button-fullwidth'
                        iconRight={<Icons icon='arrowRight' />}
                        onClick={next}
                        disabled={processing || total === 0}
                    >
                        Continue
                    </Button>
                </>
            )}
            <Alert
                type={'info'}
                header={'What is a Active Marketing contact?'}
                content={`An “active” marketing contact is someone you are actively marketing to. Transpond's paid plans are based on how many Active contacts you have. If you're not sure, you can always change this later.`}
                cta={
                    <a
                        className='quickFlex'
                        style={{ alignItems: 'center', gap: 2, fontWeight: 600, fontSize: 13, lineHeight: '20px' }}
                        onClick={showStatusGlossary}
                    >
                        Learn more
                        <Icons icon='arrowRight' style={{ width: 16, height: 16 }} />
                    </a>
                }
            />
            {/* <Button onClick={back}>Back</Button> */}
        </div>
    )
}

function Tags({ groupId, tags, selected, onChange = () => {}, onSetTotal = () => {} }) {
    const count = async () => {
        const queryObj = {
            and: [
                {
                    customFieldId: 'GroupId',
                    operator: '=',
                    value: groupId
                }
            ],
            or: []
        }
        if (selected?.length) {
            queryObj.and.push({
                customFieldId: 'subscriberTags',
                operator: '=',
                value: selected
            })
        }
        const res = await axios.post('/segment/quickQuery?limit=0&rawQuery=true&countOnly=true', {
            ignoreOrg: true,
            queryObj: [
                {
                    queryObj
                }
            ]
        })
        onSetTotal(res.data.count)
    }

    useEffect(() => {
        onSetTotal(undefined)
        count()
    }, [selected])

    if (!tags) return <LoadingIcons />

    return (
        <div>
            {tags.length > 0 && (
                <Select
                    fixedHeader='Select Tags...'
                    label={
                        <div>
                            Tags
                            <div style={{ marginTop: 4, fontWeight: 400, fontSize: 12, lineHeight: '16px' }}>
                                Contacts that have multiple selected tags will only count once.
                            </div>
                        </div>
                    }
                    filterText='Search tags...'
                    filter={true}
                    data={tags.map((t) => ({
                        label: t.tagName + ' (' + FormatNumberFuncs.formatNumber(t.count) + ')',
                        value: t.id
                    }))}
                    onChange={(e) => onChange(e.target.value)}
                />
            )}
            {tags.length === 0 && (
                <div
                    style={{
                        fontWeight: 400,
                        fontStyle: 'Regular',
                        fontSize: 13,
                        leadingTrim: 'none',
                        lineHeight: '18px',
                        letterSpacing: 0,
                        textAlign: 'center',
                        verticalAlign: 'middle'
                    }}
                >
                    You don't have any tags in Capsule yet. If you continue, everyone will be set as active marketing
                    contacts. You can update this later in Capsule or Transpond.
                </div>
            )}
            {selected?.length > 0 && (
                <div className='quickFlex mar-t12' style={{ gap: 8, flexFlow: 'wrap' }}>
                    {selected.map((tagId) => {
                        const found = tags.find((t) => t.id === tagId)
                        if (!found) return null
                        return (
                            <SquareChip
                                className='square-chip-small'
                                iconRight={
                                    <Icons
                                        icon='cross'
                                        style={{ height: 16, width: 16 }}
                                        onClick={() => onChange(tagId)}
                                    />
                                }
                            >
                                {found.tagName} ({FormatNumberFuncs.formatNumber(found.count)})
                            </SquareChip>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
